import React, { createContext, useContext, useState } from 'react';

import {
  getPaywallContext,
  isPaywallError,
} from '@/features/subscriptions/utils/paywall-error-codes';
import { handleError } from '@/shared/utils/error-handling';

type PaywallContextType = 'project' | 'organization' | 'addon';

interface PaywallState {
  isOpen: boolean;
  context: PaywallContextType | null;
  projectId: number | null;
}

interface PaywallContextValue {
  isOpen: boolean;
  context: PaywallContextType | null;
  projectId: number | null;
  openPaywall: (context: PaywallContextType, projectId?: number) => void;
  closePaywall: () => void;
  openPaywallFromError: (error: unknown, projectId?: number) => void;
}

const PaywallContext = createContext<PaywallContextValue | undefined>(
  undefined,
);

export const PaywallProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, setState] = useState<PaywallState>({
    isOpen: false,
    context: null,
    projectId: null,
  });

  function openPaywall(context: PaywallContextType, projectId?: number) {
    setState({ isOpen: true, context, projectId: projectId ?? null });
  }

  function closePaywall() {
    setState({ isOpen: false, context: null, projectId: null });
  }

  async function openPaywallFromError(error: unknown, projectId?: number) {
    const errorCode = await handleError(error);
    if (isPaywallError(errorCode)) {
      const context = getPaywallContext(errorCode);
      openPaywall(context, projectId);
    }
  }

  return (
    <PaywallContext.Provider
      value={{
        isOpen: state.isOpen,
        context: state.context,
        projectId: state.projectId,
        openPaywall,
        closePaywall,
        openPaywallFromError,
      }}
    >
      {children}
    </PaywallContext.Provider>
  );
};

export function usePaywallContext() {
  const context = useContext(PaywallContext);
  if (context === undefined) {
    throw new Error('usePaywallContext must be used within PaywallProvider');
  }
  return context;
}
